import { Component, computed, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatButtonModule } from '@angular/material/button';

import { ActivePatientService } from 'src/app/core/services/active-patient.service';
import { IPatientResponseDTO } from 'src/app/core/domain/patient';

/**
 * Multi-patient switcher pill for the global navbar.
 *
 * Renders only when the caregiver manages 2+ patients. Displays the currently
 * active patient's given name; tapping opens a mat-menu picker of all patients.
 * Selection is persisted via {@link ActivePatientService} (localStorage-backed,
 * self-contained; not currently wired to care-profile/pulse/surveys per
 * product decision — tracked in rettxweb #213).
 */
@Component({
  selector: 'app-header-patient-pill',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    MatIconModule,
    MatMenuModule,
    MatButtonModule
  ],
  templateUrl: './header-patient-pill.component.html',
  styleUrl: './header-patient-pill.component.scss'
})
export class HeaderPatientPillComponent {
  private activePatientService = inject(ActivePatientService);
  private translate = inject(TranslateService);

  readonly patients = this.activePatientService.patients;
  readonly activePatient = this.activePatientService.activePatient;
  readonly visible = this.activePatientService.hasMultiplePatients;

  /** Label shown on the pill; falls back to a translation key when empty. */
  readonly pillLabel = computed<string | null>(() => {
    const p = this.activePatient();
    return p?.given_name?.trim() || null;
  });

  /**
   * Accessible label for the pill button, in the form
   *   "Switch patient. Current: {given_name} {family_name}"
   * When no patient is active (transient state), falls back to a no-name label.
   */
  readonly ariaLabel = computed<string>(() => {
    const p = this.activePatient();
    if (!p) {
      return this.translate.instant('HEADER.PATIENT_PILL.ARIA_LABEL_NO_PATIENT');
    }
    const name = `${p.given_name ?? ''} ${p.family_name ?? ''}`.trim();
    return this.translate.instant('HEADER.PATIENT_PILL.ARIA_LABEL', { name });
  });

  trackByRettxid(_index: number, patient: IPatientResponseDTO): string {
    return patient.rettxid;
  }

  isActive(patient: IPatientResponseDTO): boolean {
    return this.activePatient()?.rettxid === patient.rettxid;
  }

  onSelect(patient: IPatientResponseDTO): void {
    if (this.isActive(patient)) return;
    this.activePatientService.setActivePatient(patient);
  }
}
